var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createProcessor, ExecutionRecorder, loadProjectFromFile, } from "@ironclad/rivet-node";
import { Pipeline, StepRun } from "@gentrace/core";
export * from "@gentrace/core";
export function runGraphInFile(path, options, pipelineSlug, waitForGentraceServer) {
    return __awaiter(this, void 0, void 0, function* () {
        const project = yield loadProjectFromFile(path);
        const { outputs, pipelineRunId, pipelineRun } = yield runGraph(project, options, pipelineSlug, waitForGentraceServer);
        return {
            outputs,
            pipelineRunId,
            pipelineRun,
        };
    });
}
export function runGraph(project, options, pipelineSlug, waitForGentraceServer) {
    return __awaiter(this, void 0, void 0, function* () {
        const processorInfo = createProcessor(project, options);
        const recorder = new ExecutionRecorder();
        const pipeline = new Pipeline({
            slug: pipelineSlug,
        });
        const runner = pipeline.start();
        recorder.record(processorInfo.processor);
        const outputs = yield processorInfo.run();
        const fullRecording = recorder.getRecording();
        const stepRuns = convertRecordingToStepRuns(fullRecording, project, options.graph);
        stepRuns.forEach((stepRun) => {
            runner.addStepRunNode(stepRun);
        });
        // If waitForGentraceServer is false, this will asynchronously send information
        // to Gentrace.
        const { pipelineRunId } = yield runner.submit({
            waitForServer: waitForGentraceServer,
        });
        // Pass recorded information
        return {
            outputs,
            pipelineRunId,
            pipelineRun: runner,
        };
    });
}
function convertRecordingToStepRuns(recording, project, graphId) {
    const partialProcessStartEndPairs = {};
    recording.events.forEach((event) => {
        var _a, _b;
        const eventType = event === null || event === void 0 ? void 0 : event.type;
        if (!eventType) {
            return;
        }
        if (eventType === "nodeStart" || eventType === "nodeFinish") {
            const processId = (_a = event === null || event === void 0 ? void 0 : event.data) === null || _a === void 0 ? void 0 : _a.processId;
            const nodeId = (_b = event === null || event === void 0 ? void 0 : event.data) === null || _b === void 0 ? void 0 : _b.nodeId;
            if (!processId) {
                return;
            }
            let existingPair = partialProcessStartEndPairs[processId];
            if (!existingPair) {
                existingPair = {};
                partialProcessStartEndPairs[processId] = existingPair;
            }
            existingPair.nodeId = nodeId;
            if (eventType === "nodeStart") {
                existingPair.start = event.ts;
                existingPair.inputs = event.data.inputs;
            }
            else {
                existingPair.end = event.ts;
                existingPair.outputs = event.data.outputs;
            }
        }
    });
    const processStartEndPairs = partialProcessStartEndPairs;
    const selectedGraph = graphId in project.graphs
        ? project.graphs[graphId]
        : Object.values(project.graphs).find((g) => { var _a; return ((_a = g.metadata) === null || _a === void 0 ? void 0 : _a.name) === graphId; });
    if (!selectedGraph) {
        throw new Error(`Graph with ID ${graphId} not found`);
    }
    // Convert to step runs
    const stepRuns = [];
    for (const [, pair] of Object.entries(processStartEndPairs)) {
        const { nodeId } = pair;
        const relatedNode = selectedGraph.nodes.find((node) => node.id === nodeId);
        const nodeType = relatedNode === null || relatedNode === void 0 ? void 0 : relatedNode.type;
        if (!nodeType ||
            !relatedNode.data ||
            nodeType === "graphInput" ||
            nodeType === "graphOutput") {
            continue;
        }
        const nodeData = relatedNode.data;
        if (relatedNode) {
            pair.modelParams = Object.assign(Object.assign({}, nodeData), { type: nodeType });
        }
        if (nodeType === "chat") {
            const modelName = nodeData.model ? nodeData.model : "";
            if (modelName.startsWith("gpt")) {
                // Convert to OpenAI Gentrace node
                const gentraceOpenAIInputs = Object.assign({}, pair.inputs);
                gentraceOpenAIInputs.messages = [
                    {
                        content: pair.inputs.prompt.value,
                        role: "user",
                    },
                ];
                const gentraceOpenAIModelParams = Object.assign({}, pair.modelParams);
                gentraceOpenAIModelParams.model = modelName;
                gentraceOpenAIModelParams.frequency_penalty =
                    pair.modelParams.frequencyPenalty || null;
                gentraceOpenAIModelParams.max_tokens =
                    pair.modelParams.maxTokens || undefined;
                gentraceOpenAIModelParams.presence_penalty =
                    pair.modelParams.presencePenalty || null;
                gentraceOpenAIModelParams.stop = pair.modelParams.stop || null;
                gentraceOpenAIModelParams.temperature =
                    pair.modelParams.temperature || null;
                gentraceOpenAIModelParams.top_p = pair.modelParams.top_p || null;
                const gentraceOpenAIOutputs = Object.assign({}, pair.outputs);
                const outputValues = Array.isArray(pair.outputs.response.value)
                    ? pair.outputs.response.value
                    : [pair.outputs.response.value];
                gentraceOpenAIOutputs.choices = outputValues.map((outputValue, index) => {
                    return {
                        index,
                        message: {
                            content: outputValue,
                            role: "assistant",
                        },
                        usage: {
                            completion_tokens: pair.outputs.responseTokens.value,
                            prompt_tokens: pair.outputs.requestTokens.value,
                            total_tokens: pair.outputs.responseTokens.value +
                                pair.outputs.requestTokens.value,
                        },
                    };
                });
                stepRuns.push(new StepRun("openai", "openai_createChatCompletion", pair.end - pair.start, new Date(pair.start).toISOString(), new Date(pair.end).toISOString(), gentraceOpenAIInputs, gentraceOpenAIModelParams, gentraceOpenAIOutputs, {}, undefined));
                continue;
            }
        }
        stepRuns.push(new StepRun("rivet", nodeType ? `rivet_operation_${nodeType}` : "rivet_operation", pair.end - pair.start, new Date(pair.start).toISOString(), new Date(pair.end).toISOString(), pair.inputs, pair.modelParams, pair.outputs, {}, undefined));
    }
    return stepRuns;
}
